import { useCallback, useMemo, useState } from 'react';
import { IOSDevice, StateBox } from '@garnet/ui';
import { useHashRoute } from './router.js';
import { useAsync } from './useAsync.js';
import { FixtureApi } from './data/FixtureApi.js';
import type { InspectorApi, InspectorTask } from './data/types.js';
import { PhoneHeader, TabBar } from './components/PhoneChrome.js';
import { Tasks } from './screens/Tasks.js';
import { Capture } from './screens/Capture.js';
import { Submit } from './screens/Submit.js';
import { History } from './screens/History.js';

const TABS = ['tasks', 'capture', 'submit', 'history'] as const;
type Tab = (typeof TABS)[number];

const TAB_META: ReadonlyArray<{ key: Tab; label: string; icon: 'briefcase' | 'scan' | 'upload' | 'history'; title: string }> = [
  { key: 'tasks', label: 'Tasks', icon: 'briefcase', title: 'Tasks' },
  { key: 'capture', label: 'Capture', icon: 'scan', title: 'Capture' },
  { key: 'submit', label: 'Submit', icon: 'upload', title: 'Sign & submit' },
  { key: 'history', label: 'History', icon: 'history', title: 'History' },
];

export function App({ api = new FixtureApi() as InspectorApi }: { api?: InspectorApi } = {}) {
  const [tab, nav] = useHashRoute<Tab>(TABS, 'tasks');
  const ctx = useMemo(() => api, [api]);
  const profileState = useAsync(() => ctx.getProfile());

  // ── Capture session (one active task at a time) ───────────────────────────
  const [activeTask, setActiveTask] = useState<InspectorTask | null>(null);
  const [captured, setCaptured] = useState<ReadonlySet<string>>(new Set());
  const [values, setValues] = useState<Record<string, string>>({});
  const [offline, setOffline] = useState(false);
  const [buffered, setBuffered] = useState(0);
  const [submittedTasks, setSubmittedTasks] = useState<ReadonlySet<string>>(new Set());

  const openTask = useCallback((task: InspectorTask): void => {
    setActiveTask(task);
    setCaptured(new Set());
    setValues({});
    nav('capture');
  }, [nav]);

  const toggleCapture = useCallback((key: string): void => {
    setCaptured((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const setValue = useCallback((key: string, value: string): void => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setCaptured((prev) => {
      const next = new Set(prev);
      if (value.trim() === '') next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const submit = useCallback((): void => {
    if (!activeTask) return;
    setSubmittedTasks((prev) => new Set(prev).add(activeTask.id));
    if (offline) setBuffered((n) => n + 1);
  }, [activeTask, offline]);

  const meta = TAB_META.find((m) => m.key === tab) ?? TAB_META[0]!;

  return (
    <div style={{ background: 'var(--paper)', minHeight: '100vh', color: 'var(--ink)', fontFamily: 'var(--body)' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 24px 64px' }}>
        <header style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--muted)' }}>
            § Inspector · field & lab PWA
          </div>
          <h1 className="display" style={{ fontSize: '1.7rem', fontWeight: 700, letterSpacing: '-.02em', margin: '.4rem 0 0', lineHeight: 1.05 }}>
            Method-templated capture
          </h1>
          <p style={{ maxWidth: '56ch', color: 'var(--muted)', marginTop: 8, fontSize: 14 }}>
            A credentialed inspector captures method-templated evidence on a phone. It works offline and syncs; a
            signed physical act resolves the unit check to attestation. Submission is gated until every required
            capture is present (PRD §10.6).
          </p>
        </header>

        <IOSDevice width={390} height={820}>
          <div style={{ minHeight: '100%', background: 'var(--paper)', color: 'var(--ink)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ height: 56, flex: 'none' }} aria-hidden="true" />
            <PhoneHeader
              title={meta.title}
              offline={offline}
              buffered={buffered}
              onToggleOffline={() => setOffline((o) => !o)}
              onBack={tab === 'capture' ? () => nav('tasks') : tab === 'submit' ? () => nav('capture') : undefined}
            />

            <div style={{ flex: 1, overflow: 'auto' }}>
              {profileState.loading && <StateBox kind="loading" title="Loading inspector" />}
              {(profileState.error || (!profileState.loading && !profileState.data)) && (
                <StateBox kind="error" title="Could not load your profile">{profileState.error}</StateBox>
              )}
              {profileState.data && (
                <>
                  {tab === 'tasks' && <Tasks api={ctx} profile={profileState.data} onOpen={openTask} />}
                  {tab === 'capture' && (
                    <Capture
                      task={activeTask}
                      captured={captured}
                      values={values}
                      onToggle={toggleCapture}
                      onValue={setValue}
                      onReview={() => nav('submit')}
                    />
                  )}
                  {tab === 'submit' && (
                    <Submit
                      task={activeTask}
                      profile={profileState.data}
                      captured={captured}
                      values={values}
                      offline={offline}
                      submitted={activeTask ? submittedTasks.has(activeTask.id) : false}
                      onSubmit={submit}
                      onBackToCapture={() => nav('capture')}
                    />
                  )}
                  {tab === 'history' && <History api={ctx} profile={profileState.data} />}
                </>
              )}
            </div>

            <TabBar tabs={TAB_META} active={tab} onChange={nav} />
          </div>
        </IOSDevice>
      </div>
    </div>
  );
}
